<template>
  <div>
    <contact-list-add-new
      :is-add-new-contact-sidebar-active.sync="isAddNewContactSidebarActive"
      :role-options="roleOptions"
      @refetch-data="refetchData"
    />
    <contact-list-edit
      :is-edit-contact-sidebar-active.sync="isEditSupplierSidebarActive"
      :contact-id.sync="contactId"
      :contact-name.sync="contactName"
      :contact-category.sync="contactCategory"
      :contact-type.sync="contactType"
      :contact-address.sync="contactAddress"
      :contact-city.sync="contactCity"
      :contact-postal-code.sync="contactPostalCode"
      :contact-phone1.sync="contactPhone1"
      :contact-phone2.sync="contactPhone2"
      :contact-fax.sync="contactFax"
      :contact-email.sync="contactEmail"
      :contact-pic-name.sync="contactPicName"
      :contact-pic-phone.sync="contactPicPhone"
      :contact-bank1.sync="contactBank1"
      :contact-bank2.sync="contactBank2"
      :contact-bank3.sync="contactBank3"
    />

    <app-page-shell
      title="Suppliers"
      subtitle="Manage supplier contacts, banking information, and import templates."
    >
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
              <input
                id="fileUpload"
                ref="file"
                name="fileUpload"
                type="file"
                accept=".xlsx, .xls, .csv"
                hidden
                @change="uploadFile()"
              >
              <b-button
                v-if="userRole == 'admin' || userRole == 'super-admin'"
                variant="success"
                class="d-inline-block mr-1"
                @click="chooseFile()"
              >
                <span class="text-nowrap">Import</span>
              </b-button>

              <b-button
                v-if="userRole == 'admin' || userRole == 'super-admin'"
                variant="outline-secondary"
                class="d-inline-block mr-1"
                @click="downloadTemplate()"
              >
                <span class="text-nowrap">Download Template</span>
              </b-button>

              <b-button
                v-if="userRole == 'admin' || userRole == 'super-admin'"
                variant="primary"
                @click="isAddNewContactSidebarActive = true"
              >
                <span class="text-nowrap">Add Supplier</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refContactListTable"
        class="position-relative"
        :items="fetchContacts"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        :busy="loading"
      >
        <template #table-busy>
          <b-skeleton-table
            :rows="perPage || 5"
            :columns="tableColumns ? tableColumns.length : 6"
            :table-props="{ bordered: true, striped: true }"
          />
        </template>

        <!-- Column: Name -->
        <template #cell(contact_name)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.contact_name }}</span>
          </div>
        </template>

        <!-- Column: City -->
        <template #cell(contact_city)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.contact_city }}</span>
          </div>
        </template>

        <!-- Column: Fax -->
        <template #cell(contact_fax)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.contact_fax }}</span>
          </div>
        </template>

        <!-- Column: Email -->
        <template #cell(contact_email)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.contact_email }}</span>
          </div>
        </template>

        <!-- Column: Phones -->
        <template #cell(contact_phone_1)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.contact_phone_1 }} / {{ data.item.contact_phone_2 }}</span>
          </div>
        </template>

        <!-- Column: DateCreated -->
        <template #cell(contact_created_time)="data">
          {{ dateSimple(data.item.contact_created_time, 'YYYY-MM-DD HH:mm:ss') }}
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item @click="editContact(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item @click="showDetail(data.item)">
              <feather-icon icon="ZoomInIcon" />
              <span class="align-middle ml-50">Detail</span>
            </b-dropdown-item>

            <b-dropdown-item @click="deleteContact(data.item.contact_id)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>

          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalContactes || (currentPage * perPage)"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </app-page-shell>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
  BBadge, BDropdown, BDropdownItem, BPagination, BSkeletonTable,
} from 'bootstrap-vue'
import axios from 'axios'
import moment from 'moment-timezone'
import { saveAs } from 'file-saver'
import vSelect from 'vue-select'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ref, onUnmounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'
import apiEndpoints from '@/constants/apiEndpoints'
import listPagePersistence from '@/mixins/listPagePersistence'
import AppPageShell from '@/views/apps/components/AppPageShell.vue'
import ContactList from './ContactList'
import contactsModule from '../contactsModule'
import ContactListAddNew from './ContactAddNew.vue'
import ContactListEdit from './ContactEdit.vue'

const userRole = ''
const contactData = null

export default {
  components: {
    ContactListEdit,
    ContactListAddNew,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSkeletonTable,
    vSelect,
    AppPageShell,
  },
  mixins: [listPagePersistence(['currentPage'])],
  setup() {
    const BRANCH_APP_STORE_MODULE_NAME = 'app-unit'

    // Register module
    if (!store.hasModule(BRANCH_APP_STORE_MODULE_NAME)) store.registerModule(BRANCH_APP_STORE_MODULE_NAME, contactsModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(BRANCH_APP_STORE_MODULE_NAME)) store.unregisterModule(BRANCH_APP_STORE_MODULE_NAME)
    })

    const isAddNewContactSidebarActive = ref(false)
    const isEditSupplierSidebarActive = ref(false)
    const contactId = ref('')
    const contactName = ref('')
    const contactCategory = ref('')
    const contactType = ref('')
    const contactAddress = ref('')
    const contactCity = ref('')
    const contactPostalCode = ref('')
    const contactPhone1 = ref('')
    const contactPhone2 = ref('')
    const contactFax = ref('')
    const contactEmail = ref('')
    const contactPicName = ref('')
    const contactPicPhone = ref('')
    const contactBank1 = ref('')
    const contactBank2 = ref('')
    const contactBank3 = ref('')

    const statusOptions = [
      { label: 'Pending', value: 'pending' },
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ]

    const {
      fetchContacts,
      fetchRoles,
      tableColumns,
      perPage,
      currentPage,
      totalContactes,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refContactListTable,
      refetchData,
      resolveContactRoleVariant,
      resolveContactRoleIcon,
      resolveContactStatusVariant,
      roleFilter,
      statusFilter,
      loading,
    } = ContactList()

    const roleOptions = [
      { label: 'Admin', value: 'admin' },
      { label: 'Author', value: 'author' },
      { label: 'Editor', value: 'editor' },
      { label: 'Maintainer', value: 'maintainer' },
      { label: 'Subscriber', value: 'subscriber' },
    ]

    return {
      contactId,
      contactName,
      contactCategory,
      contactType,
      contactAddress,
      contactCity,
      contactPostalCode,
      contactPhone1,
      contactPhone2,
      contactFax,
      contactEmail,
      contactPicName,
      contactPicPhone,
      contactBank1,
      contactBank2,
      contactBank3,
      isEditSupplierSidebarActive,
      isAddNewContactSidebarActive,
      userRole,
      contactData,
      fetchContacts,
      fetchRoles,
      tableColumns,
      perPage,
      currentPage,
      totalContactes,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refContactListTable,
      refetchData,
      avatarText,
      resolveContactRoleVariant,
      resolveContactRoleIcon,
      resolveContactStatusVariant,
      roleOptions,
      statusOptions,
      roleFilter,
      statusFilter,
      loading,
    }
  },
  mounted() {
    this.userRole = this.$cookies.get('UserRole')
    this.$root.$on('refreshTable', text => {
      if (this.$refs.refContactListTable) this.$refs.refContactListTable.refresh()
    })
  },
  methods: {
    downloadTemplate() {
      const userToken = this.$cookies.get('userToken')
      const url = `${process.env.VUE_APP_API_URL}contacts/import-template`
      axios.get(url, {
        headers: { Authorization: `Bearer ${userToken}` },
        responseType: 'blob',
      }).then(response => {
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        saveAs(blob, 'template_import_vendor.xlsx')
      })
    },
    uploadFile() {
      const firstFile = this.$refs.file.files[0]
      this.file = firstFile
      const userToken = this.$cookies.get("userToken")
      const header = {
        "Content-Type": "multipart/form-dataitem",
        Authorization: `Bearer ${userToken}`,
      }
      const formData = new FormData()

      if (typeof firstFile !== "undefined") {
        formData.append("file", firstFile, firstFile.name)
        formData.append("time", moment())
        const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_IMPORT_CONTACTS}`

        axios.post(url, formData, { headers: header }).then(response => {
          if (response.data.success === true) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Import Success",
                icon: "AlertTriangleIcon",
                variant: "success",
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Import Failed, Something went wrong on the server",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            })
          }
        })
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Import Failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        })
      }
    },
    chooseFile() {
      document.getElementById("fileUpload").click()
    },
    deleteContact(id) {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_DELETE_CONTACT}${id}`

      axios
        .get(url, { headers })
        .then(response => {
          if (response.data.success === true) {
            if (this.$refs.refContactListTable) this.$refs.refContactListTable.refresh()
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Congratulation',
                icon: 'CoffeeIcon',
                variant: 'success',
                text: 'Your data has been deleted!',
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Notification',
                icon: 'BellIcon',
                variant: 'danger',
                text: 'Something went wrong',
              },
            })
          }
        })
        .catch(err => {

        })
    },
    editContact(data) {
      const parsed = data
      this.contactId = parsed.contact_id
      this.contactName = parsed.contact_name
      this.contactAddress = parsed.contact_address
      this.contactCategory = parsed.contact_category
      this.contactType = parsed.contact_type
      this.contactCity = parsed.contact_city
      this.contactPostalCode = parsed.contact_postal_code
      this.contactPhone1 = parsed.contact_phone_1
      this.contactPhone2 = parsed.contact_phone_2
      this.contactFax = parsed.contact_fax
      this.contactEmail = parsed.contact_email
      this.contactPicName = parsed.contact_pic_name
      this.contactPicPhone = parsed.contact_pic_phone
      this.contactBank1 = parsed.contact_bank_1
      this.contactBank2 = parsed.contact_bank_2
      this.contactBank3 = parsed.contact_bank_3
      this.isEditSupplierSidebarActive = true
    },
    showDetail(item) {
      this.$cookies.set('contactDetail', item)
      this.$router.push({ name: 'apps-supplier-detail' })
    },
    dateSimple(value, format = 'YYYY-MM-DD') {
      let dateRet = ''
      if (value !== null) {
        dateRet = moment.utc(value).tz('Asia/Jakarta').format(format)
      } else {
        dateRet = null
      }
      return dateRet
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
